import { create } from 'zustand';
import { useEffect } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { getMe } from '@/api/auth';
import type { Me } from '@/types/api';
import { hydrateConsentFromMe } from '@/store/consent';
import { useMagicMoment } from '@/store/magicMoment';
import { useUnlockMilestone } from '@/store/unlockMilestone';

// Clerk owns identity (email, avatar, OAuth). FYD store owns business state
// (subscription_status, trial_used, councils_run, tos_accepted_at) loaded from /me.
interface FydUserState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  setMe: (me: Me | null) => void;
  setLoading: (b: boolean) => void;
  setError: (e: string | null) => void;
  clearAuth: () => void;
}

export const useFydUser = create<FydUserState>((set) => ({
  me: null,
  loading: false,
  error: null,
  setMe: (me) => {
    set({ me, error: null });
    // aha_forge v1: when /me carries the new fields, fan them out into the
    // dedicated stores so individual components can subscribe narrowly.
    if (me) {
      hydrateConsentFromMe(me.personalization_consent);
      if (me.magic_moments_fired) {
        useMagicMoment.getState().hydrate({
          iron_kill_first_seen: me.magic_moments_fired.iron_kill_first_seen ?? null,
          first_council_lifetime: me.magic_moments_fired.first_council_lifetime ?? null,
          report_ready_first_complete: me.magic_moments_fired.report_ready_first_complete ?? null,
        });
      }
      if (me.unlock_milestones_fired) {
        const u = me.unlock_milestones_fired;
        useUnlockMilestone.getState().hydrate({
          first_council: { firedAt: u.first_council ?? null, dismissedAt: null },
          ten_councils: { firedAt: u.ten_councils ?? null, dismissedAt: null },
          fifty_councils: { firedAt: u.fifty_councils ?? null, dismissedAt: null },
          hundred_councils: { firedAt: u.hundred_councils ?? null, dismissedAt: null },
        });
      }
    }
  },
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  clearAuth: () => set({ me: null, loading: false, error: null }),
}));

// ----------------------------------------------------------------------------
// WalletState — F-23 anti-Sybil + payment_pending FSM gating
//
// Backend stub returns m0 fail-soft values; this slice tracks the wallet bind
// status used by wallet_connect_modal + payment flows.
// ----------------------------------------------------------------------------
export interface WalletConnection {
  address: string;
  chain_id: number;
  signature: string;
  bound_at: string;
}

interface WalletState {
  connection: WalletConnection | null;
  free_sample_eligible: boolean;
  ip_rate_limit_remaining: number;
  loading: boolean;
  error: string | null;
  setConnection: (c: WalletConnection | null) => void;
  setEligibility: (eligible: boolean, remaining: number) => void;
  setLoading: (b: boolean) => void;
  setError: (msg: string | null) => void;
  clearWallet: () => void;
}

export const useWallet = create<WalletState>((set) => ({
  connection: null,
  free_sample_eligible: false,
  ip_rate_limit_remaining: 0,
  loading: false,
  error: null,
  setConnection: (connection) => set({ connection, error: null }),
  setEligibility: (eligible, remaining) =>
    set({ free_sample_eligible: eligible, ip_rate_limit_remaining: remaining }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  clearWallet: () =>
    set({
      connection: null,
      free_sample_eligible: false,
      ip_rate_limit_remaining: 0,
      loading: false,
      error: null,
    }),
}));

// Mount this once near the root after ClerkProvider is ready.
// Fetches FYD /me whenever Clerk auth becomes signed-in.
export function useSyncFydMe() {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const { user } = useUser();
  const setMe = useFydUser((s) => s.setMe);
  const setLoading = useFydUser((s) => s.setLoading);
  const setError = useFydUser((s) => s.setError);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setMe(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMe()
      .then((me) => {
        if (!cancelled) setMe(me);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, isLoaded, user?.id, setMe, setLoading, setError]);
}
